import { Box, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import NumberInput from '~/components/Inputs/NumberInput';
import { DicomColor } from '~/domain/dicom/dicomColor';
import {
    dicomColorsEqual,
    dicomColorToHex,
    parseHexColor,
} from '~/utils/colorUtils';

/** Each channel and the message id of its accessible name. */
const CHANNELS = [
    ['R', 'colorPicker.channel.red'],
    ['G', 'colorPicker.channel.green'],
    ['B', 'colorPicker.channel.blue'],
] as const;

/**
 * A Hex field over R, G and B number fields for an 8-bit colour, in step
 * with `color`.
 *
 * A field reports a colour only once its text is valid — Hex as `#RGB` or
 * `#RRGGBB`, a channel as an integer 0–255 — and nothing is clamped to
 * make typed text valid. The Hex field keeps the text typed into it until
 * it is left holding valid text; invalid Hex text stays, marked, until it
 * is corrected. A channel field marks an entry that is not an integer
 * 0–255 while it is being typed; meanwhile, and when it is left that way,
 * the colour holds the value the channel had when the field was entered
 * (see the number field's `commitOnChange`). `onValidityChange` hears whether
 * any field holds invalid text, so a caller can block accepting a colour
 * the fields do not show.
 */
export default function ColorValueFields({
    color,
    onChange,
    onValidityChange,
}: {
    color: DicomColor;
    onChange: (color: DicomColor) => void;
    onValidityChange?: (isValid: boolean) => void;
}) {
    const intl = useIntl();
    const [hexDraft, setHexDraft] = useState<string>();
    const [channelValid, setChannelValid] = useState({
        R: true,
        G: true,
        B: true,
    });
    const hexInvalid =
        hexDraft !== undefined && parseHexColor(hexDraft) === null;
    const isValid = !hexInvalid && Object.values(channelValid).every(Boolean);
    useEffect(() => onValidityChange?.(isValid), [isValid, onValidityChange]);

    return (
        <Box display="grid" gap={1.5}>
            <TextField
                size="small"
                fullWidth
                label={intl.formatMessage({ id: 'colorPicker.hex' })}
                value={hexDraft ?? dicomColorToHex(color).slice(1)}
                onChange={({ target: { value: text } }) => {
                    setHexDraft(text);
                    const next = parseHexColor(text);
                    if (next && !dicomColorsEqual(next, color)) {
                        onChange(next);
                    }
                }}
                onBlur={() => hexInvalid || setHexDraft(undefined)}
                error={hexInvalid}
                inputProps={{
                    'aria-label': intl.formatMessage({
                        id: 'colorPicker.hexLabel',
                    }),
                    spellCheck: false,
                    autoComplete: 'off',
                    'data-testid': 'color-field-hex',
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            disableTypography
                            sx={{ mr: 0, color: 'text.secondary' }}
                        >
                            #
                        </InputAdornment>
                    ),
                }}
                sx={{
                    // Room for the label, which sits above the field's edge.
                    mt: 0.75,
                    // The `#` reads as part of the value: same size, and
                    // starting where the number fields' text starts.
                    '& .MuiOutlinedInput-root': { pl: 1 },
                    '& .MuiInputAdornment-root': { fontSize: 'inherit' },
                    '& .MuiOutlinedInput-root, & .MuiInputBase-input': {
                        fontSize: 14,
                    },
                    '& .MuiInputBase-input': { pl: 0.25 },
                }}
            />
            <Box display="flex" gap={1}>
                {CHANNELS.map(([channel, nameId]) => (
                    <NumberInput
                        key={channel}
                        label={channel}
                        value={color[channel]}
                        onChange={(value) =>
                            value !== null &&
                            onChange({ ...color, [channel]: value })
                        }
                        onValidityChange={(valid) =>
                            setChannelValid((all) => ({
                                ...all,
                                [channel]: valid,
                            }))
                        }
                        min={0}
                        max={255}
                        step={1}
                        precision={0}
                        commitOnChange
                        fullWidth
                        // Below the field: above it the range hint would
                        // cover the Hex value.
                        tooltipPlacement="bottom-start"
                        inputProps={{
                            'aria-label': intl.formatMessage({ id: nameId }),
                            inputMode: 'numeric',
                            'data-testid': `color-field-${channel}`,
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}
